import { useCallback, useState } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import { Button, Modal, Select, Textarea } from '@indipia/ui-core';

export interface ReasonOption {
  code: string;
  label: string;
}

export interface ReasonSelectModalProps {
  open: boolean;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (reasonCode: string, reasonText?: string) => void;
  /** 모달 제목 (예: "환불 사유를 알려주세요") */
  title: string;
  /** Select 라벨 (예: "환불 사유") */
  selectLabel: string;
  /** 이 코드를 선택했을 때만 자유입력 textarea를 보여준다 */
  etcCode: string;
  confirmLabel: string;
  confirmVariant: 'primary' | 'danger';
  /** 사유 목록 조회 — 화면마다 다른 API(getRefundReasons/getCancelReasons)를 주입받는다 */
  queryKey: QueryKey;
  fetchReasons: (signal: AbortSignal) => Promise<ReasonOption[]>;
  /** "기타" 자유입력 글자수 제한 — DB 컬럼 길이와 맞춰야 하는 화면(환불)만 넘긴다. 없으면 카운터도 안 보여준다 */
  reasonTextMaxLength?: number;
  /** 버튼 위에 보여줄 추가 안내문 (예: 환불의 7일 이내 전액환불 안내) */
  footerNote?: string;
}

/**
 * "사유 선택 + (기타면) 자유입력 + 확인" 팝업 공용 컴포넌트 — RefundReasonModal·CancelReasonModal이
 * Select 위젯·BE 공통코드 조회로 같은 패턴이 된 뒤(2026-09-17) 텍스트·API만 다르고 나머지 구조가
 * 100% 동일해 하나로 합쳤다(사용자 지적 — 리액트에서 컴포넌트를 굳이 두 벌 둘 이유가 없음).
 */
export function ReasonSelectModal({
  open,
  submitting,
  onClose,
  onConfirm,
  title,
  selectLabel,
  etcCode,
  confirmLabel,
  confirmVariant,
  queryKey,
  fetchReasons,
  reasonTextMaxLength,
  footerNote,
}: ReasonSelectModalProps) {
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');

  const reasonsQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchReasons(signal),
    enabled: open,
    // 공통코드라 자주 안 바뀌지만, 운영자가 세션 중 코드를 비활성화·수정할 수 있어 Infinity 대신
    // 유한 staleTime을 둔다 — 모달을 다시 열 때마다 매번 재조회하진 않되, 5분 넘게 지나면
    // 백그라운드로 갱신해 오래된 선택지를 계속 붙들고 있지 않는다.
    staleTime: 5 * 60 * 1000,
  });
  const reasonOptions = reasonsQuery.data ?? [];
  // 조회 자체는 성공(200)했는데 목록이 비어있는 경우 — Zeus에 공통코드그룹이 아직 등록 안 됐을 때
  // BE가 빈 배열을 정상 응답으로 내려준다(SubscriptionService.getRefundReasonOptions/
  // getCancelReasonOptions 주석 참고). isError로는 안 잡혀서 그냥 두면 "사유를 선택해주세요" 문구만
  // 계속 보이고 이유를 알 수 없이 확인 버튼이 영원히 비활성 상태로 남는다(2026-09-17 검수에서 발견).
  const isEmpty = reasonsQuery.isSuccess && reasonOptions.length === 0;

  // useCallback으로 참조 고정 — inline 함수면 호출부(페이지)가 리렌더될 때마다 새 함수가 만들어져
  // Modal의 포커스 트랩 effect(onClose 의존)가 재실행되고, "기타" 텍스트 입력 중 포커스가 닫기[x]
  // 버튼으로 튀는 버그가 있었다(2026-09-17 발견). 호출부의 onClose 자체도 memoize돼 있어야
  // 이 메모이제이션이 끝까지 유효하다 — RefundRequestPage.tsx·SubscriptionManagePage.tsx 참고.
  const handleClose = useCallback(() => {
    setReasonCode('');
    setReasonText('');
    onClose();
  }, [onClose]);

  function handleConfirm() {
    if (!reasonCode) return;
    onConfirm(reasonCode, reasonCode === etcCode ? reasonText : undefined);
  }

  return (
    <Modal open={open} onClose={handleClose} title={title}>
      <div className="flex flex-col gap-sm">
        <Select
          label={selectLabel}
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
          disabled={reasonsQuery.isLoading || isEmpty}
        >
          <option value="" disabled>
            {reasonsQuery.isLoading ? '불러오는 중...' : isEmpty ? '선택 가능한 사유가 없어요' : '사유를 선택해주세요'}
          </option>
          {reasonOptions.map((opt) => (
            <option key={opt.code} value={opt.code}>
              {opt.label}
            </option>
          ))}
        </Select>

        {reasonsQuery.isError && (
          <p className="text-caption text-danger">사유 목록을 불러오지 못했어요. 잠시 후 다시 시도해주세요.</p>
        )}
        {isEmpty && (
          <p className="text-caption text-danger">사유 목록을 준비 중이에요. 고객센터로 문의해주세요.</p>
        )}

        {reasonCode === etcCode && (
          <div>
            <Textarea
              placeholder="사유를 직접 입력해주세요"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              maxLength={reasonTextMaxLength}
            />
            {reasonTextMaxLength != null && (
              <p className="mt-01 text-right text-caption text-text-muted">
                {reasonText.length}/{reasonTextMaxLength}
              </p>
            )}
          </div>
        )}

        {footerNote && <p className="text-caption text-text-muted">{footerNote}</p>}

        <div className="mt-03 flex w-full gap-sm">
          <Button variant="secondary" className="flex-1" onClick={handleClose} disabled={submitting}>
            취소
          </Button>
          <Button
            variant={confirmVariant}
            className="flex-1"
            onClick={handleConfirm}
            disabled={!reasonCode || submitting}
            loading={submitting}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
